/**
 * La « Réf. GHP » que le serveur **attribue** à une demande créée sans référence — `GHP-2026-0001`.
 *
 * ⚠️ **Une attribution, pas une contrainte** (M1). La colonne reste un champ libre sans `UNIQUE` :
 * une référence fournie — reprise de l'existant, import d'un document, saisie du gestionnaire —
 * est conservée telle quelle, doublons compris. Ce module ne sert que le cas où personne n'en a
 * donné.
 *
 * ⚠️ **Le repli, pas le format normal** : depuis #406, une demande reçoit d'abord la référence au
 * format de GHP, composée de la commune et de la parcelle (`ghp-reference.ts`). `GHP-<année>-<rang>`
 * sert les ventes, et les demandes dont la commune n'a pas de préfixe ou la parcelle pas de forme
 * exploitable : il se numérote sans rien lire d'autre que les références déjà attribuées.
 *
 * ⚠️ **L'année est celle de Paris** : une demande créée le 31 décembre à 23 h 30 est de l'année qui
 * s'achève, alors que le conteneur, en UTC, la daterait déjà de la suivante.
 */
export const INQUIRY_REFERENCE_PREFIX = 'GHP';
const REFERENCE_TIME_ZONE = 'Europe/Paris';
/** Le rang s'écrit sur quatre chiffres au moins — la forme du seed —, et s'allonge au-delà. */
const RANK_WIDTH = 4;
/**
 * Neuf chiffres au plus : c'est ce qui garantit que le rang lu en base tient dans un `integer`.
 * Une référence saisie à la main sur dix chiffres (`GHP-2026-0000000001`) est simplement ignorée
 * du comptage, au lieu de faire échouer la création sur un dépassement.
 */
const MAX_RANK_DIGITS = 9;
const yearFormat = new Intl.DateTimeFormat('fr-FR', {
    timeZone: REFERENCE_TIME_ZONE,
    year: 'numeric',
});
/** L'année civile de Paris à cet instant — celle que porte la référence attribuée. */
export function inquiryReferenceYear(at) {
    return Number(yearFormat.format(at));
}
/**
 * `GHP-2026-0001`. ⚠️ `padStart` complète sans jamais tronquer : le 10 000ᵉ rang s'écrit `10000`,
 * là où un `lpad` SQL l'aurait coupé en `1000` — un doublon fabriqué par le formatage.
 */
export function formatInquiryReference(year, rank) {
    return `${INQUIRY_REFERENCE_PREFIX}-${String(year)}-${String(rank).padStart(RANK_WIDTH, '0')}`;
}
/**
 * Le motif POSIX (opérateur `~` de PostgreSQL) des références attribuées une année donnée, le
 * rang en premier groupe capturant.
 *
 * ⚠️ **Ancré aux deux bouts** : `GHP-2026-0001-CV`, la contre-visite suffixée à la main (M1), n'est
 * pas un rang — la compter ferait croire que 0001 est encore libre, ou l'inverse selon l'ordre.
 */
export function inquiryReferencePattern(year) {
    return `^${INQUIRY_REFERENCE_PREFIX}-${String(year)}-([0-9]{1,${String(MAX_RANK_DIGITS)}})$`;
}
//# sourceMappingURL=reference.js.map